import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

// ─── Flytthjälp Kungsbacka ─────────────────────────────────────────
export const flytthjalpKungsbackaPage: ServicePageConfig = {
  slug: "flytthjalp-kungsbacka",
  serviceCategory: "flytt",
  city: "göteborg",

  meta: {
    title: "Flytthjälp Kungsbacka – Skåpbil och Bärare | Ungdomskraft",
    description:
      "Flytthjälp i Kungsbacka, Onsala, Särö, Kullavik, Fjärås och Åsa. Vi bär, kör och ställer på plats. Fast paketpris, RUT-avdrag på fakturan.",
    h1: "Flytthjälp Kungsbacka",
  },

  hero: {
    heading: "Flytthjälp i Kungsbacka",
    intro:
      "Från lägenheten i Aranäs eller centrum till villan på Onsala, från Särö till Fjärås eller hela vägen från Göteborg – vi tar din flytt i Kungsbacka med skåpbil och unga, starka bärare. Välj paket, se priset direkt och låt oss sköta det tunga. RUT-avdraget dras på fakturan.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft bär flyttkartonger till skåpbil vid flytthjälp i Kungsbacka",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har hjälpt till med flyttar i Göteborgsregionen sedan 2014. Kungsbacka är regionens sydligaste kommun och en av de största, och vi kör hit längs E6 med allt från studentbohag till fulla villor.",
    bookYourText: "Så bokar du",
    bookDescText:
      "Skriv in från- och tilladress, välj paket efter hur mycket du har och boka en tid. Du ser priset innan du bekräftar.",
    vehicleTitleText: "Transport ingår",
    vehicleDescText:
      "Skåpbilen, bärarna och skyddsmaterialet är med i priset. Vi lastar, kör och lossar – du behöver inte ordna något eget fordon.",
  },

  itemsSection: {
    heading: "Det här bär vi vid flytt i Kungsbacka",
    items: [
      {
        name: "Soffor & loungemöbler",
        description: "Vardagsrumssoffor, fåtöljer och utemöbler från altanen",
        image: "/sofa.webp",
        alt: "Soffa som lastas vid flytthjälp i Kungsbacka",
      },
      {
        name: "Sängar",
        description: "Kontinentalsängar, barnsängar och gästsängar",
        image: "/bed.webp",
        alt: "Säng som bärs ut från villa på Onsala, Kungsbacka",
      },
      {
        name: "Bord",
        description: "Matbord, arbetsbord och trädgårdsbord",
        image: "/table.webp",
        alt: "Matbord som flyttas från hus i Kullavik, Kungsbacka",
      },
      {
        name: "Tvätt & kök",
        description: "Tvättmaskin, torktumlare, kyl och frys",
        image: "/washer.webp",
        alt: "Tvättmaskin som bärs vid flytt i Aranäs, Kungsbacka",
      },
      {
        name: "Garderober",
        description: "Hela garderobssystem som tas isär, byråer och skänkar",
        image: "/wardrobe.webp",
        alt: "Garderob som monteras isär inför flytt i Särö, Kungsbacka",
      },
      {
        name: "Kartonger",
        description: "Flyttlådor, förvaringsboxar och saker från garaget",
        image: "/box.webp",
        alt: "Flyttkartonger som lastas vid flytt i Fjärås, Kungsbacka",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Kungsbacka – kust, villabygd och växande stadsdelar",
    mainText:
      "Kungsbacka är Hallands nordligaste kommun och har runt 86 500 invånare, vilket gör den till den näst största kommunen i Göteborgsregionen. Bostäderna är till stor del villor och radhus: Onsalahalvön, Särö, Kullavik och Vallda längs kusten, och Fjärås, Anneberg och Gällinge i inlandet. I staden finns också lägenheter, bland annat i Aranäs och i centrala Kungsbacka, där den gamla trästaden fortfarande står kring torget. En flytt i Kungsbacka handlar därför oftast om ett helt hus med garage och förråd, och mer sällan om trappor utan hiss.\n\nDet vanligaste flyttmönstret är att en familj lämnar en lägenhet i Göteborg eller Mölndal för ett hus i Kungsbacka. Avståndet in till Göteborg är ungefär tre mil längs E6, så sträckan blir längre än de 20 km som ingår i paketet. Km-priset räknas ut direkt i bokningen, och du ser totalen innan du bekräftar. Vi flyttar också många inom kommunen – till exempel från ett hus på Onsala till en lägenhet i centrum när trädgården blivit för mycket att sköta.\n\nPendeltåget från Göteborg har sin ändstation i Kungsbacka och stannar också i Anneberg och Hede, så många som flyttar hit fortsätter pendla med tåg. För oss betyder det att vi planerar in körningen efter E6-trafiken i rusningstid och tar Säröleden när flytten går till kustorterna i norr. Ute på Onsala och i Särö är villavägarna ofta smala och tomterna kuperade, så vi frågar alltid hur nära huset skåpbilen kan stå.",
    preparationHeading: "Inför flytten i Kungsbacka",
    preparationText:
      "Gå igenom garaget, förrådet och eventuellt båthus i god tid – i villorna längs kusten är det där de mest oväntade sakerna brukar finnas. Bestäm vad som ska flyttas, säljas eller slängas. Packa sedan köket sist och sovrummen först, och märk varje kartong med rum.\n\nSe till att uppfarten är fri från bilar och släp på flyttdagen så att skåpbilen kan backa nära entrén. Flyttar du till eller från en lägenhet i Aranäs eller centrum, fråga om hissen kan reserveras och var närmaste lastplats ligger. Frosta av frysen dagen innan och ha nycklar och portkoder till båda adresserna redo.",
    additionalSections: [
      {
        heading: "Flytta från Göteborg till Kungsbacka",
        content:
          "Vi bär ut och lastar i lägenheten i Göteborg, kör söderut på E6 förbi Mölndal och Kållered och är framme i Kungsbacka efter ungefär en halvtimme utan köer. Där bär vi in möblerna rum för rum och sätter ihop sängar och garderober. Är bohaget större än en last planerar vi två vändor redan från början, så att dagen inte drar ut.",
        cta: { text: "Se flytthjälp i Göteborg →", href: "/flytthjalp-goteborg" },
      },
      {
        heading: "Orter och stadsdelar vi flyttar i",
        content:
          "Vi flyttar i hela Kungsbacka kommun. I och runt staden: centrum och trästaden, Aranäs, Hede, Kolla, Tölö och Varla. Längs kusten: Onsala, Särö, Kullavik och Vallda i norr samt Åsa och Frillesås i söder. I inlandet: Fjärås, Anneberg, Gällinge och Hällingsjö.\n\nFlytthjälp är en RUT-tjänst – du får 50 % avdrag på arbetskostnaden, som mest 75 000 kr per person och år. Vi gör avdraget direkt på fakturan.",
      },
      {
        heading: "Varför boka flytthjälp i Kungsbacka med Ungdomskraft?",
        listItems: [
          {
            label: "Rätt för villaflytt –",
            text: "De flesta flyttar i Kungsbacka gäller hus. Vi planerar för garage, förråd och flera vändor från början i stället för att upptäcka dem på plats.",
          },
          {
            label: "Tydligt pris trots längre sträcka –",
            text: "Km-priset för flytten från Göteborg syns i bokningen. Inga påslag efteråt.",
          },
          {
            label: "Hela kommunen –",
            text: "Från Kullavik i norr till Frillesås i söder och Hällingsjö i inlandet.",
          },
          {
            label: "Försäkrad flytt –",
            text: "Trygg Hansa försäkrar både dina saker och huset vi bär i.",
          },
          {
            label: "Alla dagar –",
            text: "Vi flyttar måndag till söndag och har ofta lediga tider samma vecka.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Kungsbacka?",
      answer:
        "Våra paket går från Liten (995 kr) för en eller två möbler upp till Mega Stor (8 995 kr, upp till 8 timmars arbete) för ett helt hus. En lägenhet brukar bli Extra Stor (4 995 kr, upp till 4 timmar). Inom 20 km ingår körningen, därefter tillkommer ett km-pris som syns i bokningen.",
    },
    {
      question: "Vad kostar en flytt från Göteborg till Kungsbacka?",
      answer:
        "Sträckan är ungefär tre mil, så utöver paketpriset tillkommer ett km-pris för det som går över 20 km. Km-priset beror på paketet (8–22 kr/km för skåpbilspaketen) och räknas in automatiskt innan du bokar.",
    },
    {
      question: "Kan jag göra RUT-avdrag för flytthjälp i Kungsbacka?",
      answer:
        "Ja. Flytthjälp ger 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar det på fakturan.",
    },
    {
      question: "Flyttar ni villor på Onsala, Särö och Kullavik?",
      answer:
        "Ja, det är några av våra vanligaste uppdrag i kommunen. Vi tömmer hus, garage och förråd och frågar alltid hur nära huset bilen kan stå, eftersom vägarna där ofta är smala.",
    },
    {
      question: "Hjälper ni till i Fjärås, Åsa och Frillesås?",
      answer:
        "Ja, vi flyttar i hela kommunen, även i de södra kustorterna och inlandet. Längre bort från Göteborg kan körsträckan bli längre, och det syns då i priset.",
    },
    {
      question: "Kan ni flytta till en lägenhet i centrala Kungsbacka?",
      answer:
        "Ja. Vi bär i trappa när det saknas hiss, utan extra avgift. Tala om våning och om hissen kan bokas när du beställer.",
    },
    {
      question: "Ingår skåpbilen?",
      answer: "Ja, skåpbil och personal ingår i alla våra flyttpaket.",
    },
    {
      question: "Flyttar ni trädgårdsmöbler och utrustning från garaget?",
      answer:
        "Ja, utemöbler, cyklar, grillar och annat från garage och förråd följer med. Ta med dem när du väljer paket, så räcker tiden till.",
    },
    {
      question: "Demonterar ni möbler?",
      answer:
        "Ja, vi tar isär och sätter ihop sängar, garderober och bord som behöver det.",
    },
    {
      question: "Kan jag boka flytthjälp i Kungsbacka en lördag eller söndag?",
      answer: "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Är flytten försäkrad?",
      answer: "Ja, alla flyttar är försäkrade genom Trygg Hansa.",
    },
    {
      question: "Kan ni köra bort det som inte ska med?",
      answer:
        "Ja, vi gör även bortforsling i Kungsbacka. Det du inte vill ta med kör vi till återvinningen.",
    },
    {
      question: "Vilka orter i Kungsbacka kommun täcker ni?",
      answer:
        "Hela kommunen: centrum, Aranäs, Hede, Kolla, Tölö, Varla, Onsala, Särö, Kullavik, Vallda, Åsa, Frillesås, Fjärås, Anneberg, Gällinge och Hällingsjö.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Hela Göteborgsregionen: ${goteborgKommunerText}. Flytt mellan kommunerna, till exempel från Mölndal till Kungsbacka, bokar du precis som vanligt.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Kungsbacka steg för steg",
    step2Text:
      "Bärarna tömmer din gamla bostad, lastar skåpbilen och kör till Kungsbacka eller till den adress du flyttar till. Garage och förråd tar vi i samma vända.",
    step3Title: "Inflyttat",
    step3Desc:
      "Möblerna står där de ska, sängarna är ihopsatta och kartongerna ligger i rätt rum. RUT-avdraget är redan gjort på fakturan.",
    ctaHeading: "Boka flytthjälp i Kungsbacka",
    ctaText:
      "Ange adresserna och hur mycket som ska flyttas – priset, med körsträcka, syns direkt.",
  },

  en: {
    slug: "moving-help-kungsbacka",
    meta: {
      title: "Moving Help Kungsbacka – Van and Carriers | Ungdomskraft",
      description:
        "Moving help in Kungsbacka, Onsala, Särö, Kullavik, Fjärås and Åsa. We carry, drive and set up. Fixed package price, RUT deduction on the invoice.",
      h1: "Moving Help Kungsbacka",
    },
    hero: {
      heading: "Moving Help in Kungsbacka",
      intro:
        "From the flat in Aranäs or the centre to the house on Onsala, from Särö to Fjärås or all the way from Gothenburg – we handle your move in Kungsbacka with a van and young, strong carriers. Choose a package, see the price instantly and let us do the heavy work. The RUT deduction is taken off the invoice.",
      heroImageAlt: "Ungdomskraft carrying moving boxes to a van during moving help in Kungsbacka",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has helped with moves in the Gothenburg region since 2014. Kungsbacka is the region's southernmost municipality and one of the largest, and we drive here along the E6 with everything from student belongings to full houses.",
      bookYourText: "How to book",
      bookDescText:
        "Enter the from and to address, choose a package based on how much you have and book a time. You see the price before you confirm.",
      vehicleTitleText: "Transport included",
      vehicleDescText:
        "The van, the carriers and the protective material are in the price. We load, drive and unload – you don't need to arrange a vehicle of your own.",
    },
    itemsSection: {
      heading: "What we carry when moving in Kungsbacka",
      items: [
        {
          name: "Sofas & lounge furniture",
          description: "Living room sofas, armchairs and outdoor furniture from the deck",
          image: "/sofa.webp",
          alt: "Sofa being loaded during moving help in Kungsbacka",
        },
        {
          name: "Beds",
          description: "Continental beds, children's beds and guest beds",
          image: "/bed.webp",
          alt: "Bed carried out of a house on Onsala, Kungsbacka",
        },
        {
          name: "Tables",
          description: "Dining tables, work tables and garden tables",
          image: "/table.webp",
          alt: "Dining table moved from a house in Kullavik, Kungsbacka",
        },
        {
          name: "Laundry & kitchen",
          description: "Washing machine, tumble dryer, fridge and freezer",
          image: "/washer.webp",
          alt: "Washing machine carried during a move in Aranäs, Kungsbacka",
        },
        {
          name: "Wardrobes",
          description: "Whole wardrobe systems taken apart, dressers and sideboards",
          image: "/wardrobe.webp",
          alt: "Wardrobe disassembled before a move in Särö, Kungsbacka",
        },
        {
          name: "Boxes",
          description: "Moving boxes, storage boxes and things from the garage",
          image: "/box.webp",
          alt: "Moving boxes loaded during a move in Fjärås, Kungsbacka",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Kungsbacka – coast, house country and growing districts",
      mainText:
        "Kungsbacka is Halland's northernmost municipality with around 86,500 residents, making it the second largest municipality in the Gothenburg region. Homes are largely houses and terraces: the Onsala peninsula, Särö, Kullavik and Vallda along the coast, and Fjärås, Anneberg and Gällinge inland. The town also has flats, for example in Aranäs and central Kungsbacka, where the old wooden town still stands around the square. A move in Kungsbacka is therefore usually about a whole house with a garage and shed, and less often about stairs without a lift.\n\nThe most common move pattern is a family leaving a flat in Gothenburg or Mölndal for a house in Kungsbacka. The distance into Gothenburg is roughly 30 km along the E6, so the trip is longer than the 20 km included in the package. The per-km price is calculated directly in the booking, and you see the total before you confirm. We also move many people within the municipality – for example from a house on Onsala to a flat in the centre when the garden has become too much to look after.\n\nThe commuter train from Gothenburg terminates in Kungsbacka and also stops at Anneberg and Hede, so many who move here keep commuting by train. For us it means we plan the drive around E6 rush-hour traffic and take Säröleden when the move goes to the coastal villages in the north. Out on Onsala and in Särö the residential roads are often narrow and the plots hilly, so we always ask how close to the house the van can park.",
      preparationHeading: "Before your move in Kungsbacka",
      preparationText:
        "Go through the garage, shed and any boathouse in good time – in the houses along the coast that is where the most unexpected things tend to be. Decide what is moving, being sold or thrown away. Then pack the kitchen last and the bedrooms first, and label every box by room.\n\nMake sure the driveway is clear of cars and trailers on moving day so the van can reverse close to the entrance. If you are moving to or from a flat in Aranäs or the centre, ask whether the lift can be reserved and where the nearest loading spot is. Defrost the freezer the day before and have keys and entry codes for both addresses ready.",
      additionalSections: [
        {
          heading: "Moving from Gothenburg to Kungsbacka",
          content:
            "We carry out and load at the flat in Gothenburg, drive south on the E6 past Mölndal and Kållered and reach Kungsbacka after about half an hour without queues. There we carry the furniture in room by room and put beds and wardrobes back together. If the household is bigger than one load, we plan two trips from the start so the day doesn't drag on.",
          cta: { text: "See moving help in Gothenburg →", href: "/en/moving-help-gothenburg" },
        },
        {
          heading: "Towns and districts we move in",
          content:
            "We move across the whole of Kungsbacka municipality. In and around town: the centre and the wooden town, Aranäs, Hede, Kolla, Tölö and Varla. Along the coast: Onsala, Särö, Kullavik and Vallda in the north, and Åsa and Frillesås in the south. Inland: Fjärås, Anneberg, Gällinge and Hällingsjö.\n\nMoving help is a RUT service – you get 50% off the labour cost, at most SEK 75,000 per person per year. We apply the deduction directly on the invoice.",
        },
        {
          heading: "Why book moving help in Kungsbacka with Ungdomskraft?",
          listItems: [
            {
              label: "Built for house moves –",
              text: "Most moves in Kungsbacka involve houses. We plan for garages, sheds and several trips from the start instead of discovering them on site.",
            },
            {
              label: "Clear price despite the distance –",
              text: "The per-km price for the move from Gothenburg shows in the booking. No surcharges afterwards.",
            },
            {
              label: "The whole municipality –",
              text: "From Kullavik in the north to Frillesås in the south and Hällingsjö inland.",
            },
            {
              label: "Insured move –",
              text: "Trygg Hansa insures both your belongings and the house we carry in.",
            },
            {
              label: "Every day –",
              text: "We move Monday to Sunday and often have slots the same week.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Kungsbacka cost?",
        answer:
          "Our packages range from Small (SEK 995) for one or two pieces of furniture up to Mega Large (SEK 8,995, up to 8 hours of work) for a whole house. A flat usually becomes Extra Large (SEK 4,995, up to 4 hours). Driving within 20 km is included, after which a per-km price shown in the booking is added.",
      },
      {
        question: "What does a move from Gothenburg to Kungsbacka cost?",
        answer:
          "The distance is roughly 30 km, so on top of the package price a per-km price is added for the part above 20 km. The per-km price depends on the package (SEK 8–22/km for the van packages) and is included automatically before you book.",
      },
      {
        question: "Can I claim the RUT deduction for moving help in Kungsbacka?",
        answer:
          "Yes. Moving help gives 50% off the labour cost, up to SEK 75,000 per person per year. We deduct it on the invoice.",
      },
      {
        question: "Do you move houses on Onsala, Särö and Kullavik?",
        answer:
          "Yes, those are some of our most common jobs in the municipality. We empty houses, garages and sheds and always ask how close to the house the van can park, as the roads there are often narrow.",
      },
      {
        question: "Do you help in Fjärås, Åsa and Frillesås?",
        answer:
          "Yes, we move across the whole municipality, including the southern coastal villages and inland. Further from Gothenburg the driving distance can be longer, and that then shows in the price.",
      },
      {
        question: "Can you move me into a flat in central Kungsbacka?",
        answer:
          "Yes. We carry on the stairs when there is no lift, at no extra charge. Tell us the floor and whether the lift can be booked when you order.",
      },
      {
        question: "Is the van included?",
        answer: "Yes, a van and crew are included in all our moving packages.",
      },
      {
        question: "Do you move garden furniture and things from the garage?",
        answer:
          "Yes, outdoor furniture, bikes, barbecues and other things from the garage and shed come along. Include them when you choose a package so there is enough time.",
      },
      {
        question: "Do you disassemble furniture?",
        answer:
          "Yes, we take apart and put back together beds, wardrobes and tables that need it.",
      },
      {
        question: "Can I book moving help in Kungsbacka on a Saturday or Sunday?",
        answer: "Yes, we move every day of the week.",
      },
      {
        question: "Is the move insured?",
        answer: "Yes, every move is insured through Trygg Hansa.",
      },
      {
        question: "Can you take away what isn't coming along?",
        answer:
          "Yes, we also do junk removal in Kungsbacka. Whatever you don't want to take along, we drive to recycling.",
      },
      {
        question: "Which towns in Kungsbacka municipality do you cover?",
        answer:
          "The whole municipality: the centre, Aranäs, Hede, Kolla, Tölö, Varla, Onsala, Särö, Kullavik, Vallda, Åsa, Frillesås, Fjärås, Anneberg, Gällinge and Hällingsjö.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `The whole Gothenburg region: ${goteborgKommunerText}. A move between municipalities, for example from Mölndal to Kungsbacka, is booked just like any other.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Kungsbacka step by step",
      step2Text:
        "The carriers empty your old home, load the van and drive to Kungsbacka or the address you are moving to. Garage and shed are done in the same trip.",
      step3Title: "Moved in",
      step3Desc:
        "Furniture stands where it should, beds are assembled and boxes are in the right rooms. The RUT deduction is already applied on the invoice.",
      ctaHeading: "Book moving help in Kungsbacka",
      ctaText:
        "Enter the addresses and how much is moving – the price, including distance, shows instantly.",
    },
  },
};
